import { useState, useMemo, memo } from "react";
import type {
  BiddingInfoItem,
  BiddingInfoSubItem,
  MasterDataForComponents,
} from "../../../types";
import type { ItemStatusType } from "../../../constants";
import {
  BiddingItemStatusConst,
  CaltivationCategoryOptions,
  ListingCategoryConst,
  ProductionHistoryOptions,
  statusOptions,
} from "../../../constants";
import { useAuth } from "../../../contexts/AuthContext";
import { formatToDateInput } from "../../../utils/date";
import { BiddingItemMaintenanceEditForm } from "./BiddingItemMaintenanceEditForm";
import { BiddingItemMaintenanceSplitForm } from "./BiddingItemMaintenanceSplitForm";
import { BiddingItemMaintenanceSubItemRow } from "./BiddingItemMaintenanceSubItemRow";
import { UserRole } from "../../../types/UserRole";
import { useTruncatedTooltip } from "../../common/TruncatedTooltip";
import { Pencil, Trash2, Copy } from "lucide-react";
import Big from "big.js";
import { TEA_CATEGORY } from "../../../constants/TeaCategoryConst";
//----------------
// Props
//----------------
interface BiddingItemMaintenanceItemRowProps {
  item: BiddingInfoItem;
  isSplitting: boolean;
  isEditing: boolean;
  isSaving?: boolean;
  onToggleSplit: () => void;
  onToggleEdit: () => void;
  onSaveEdit: (updatedItem: BiddingInfoItem) => void;
  onConfirmSplit: (newSubItems: BiddingInfoSubItem[]) => void;
  onStatusChange: (newStatus: ItemStatusType) => void;
  onDelete: () => void;
  onCopy: (item: BiddingInfoItem) => void;
  masterData: MasterDataForComponents;
  isSelected?: boolean;
  onToggleSelect?: () => void;
  isCheckable?: boolean;
  isSeller: boolean;
  isFarmer: boolean;
}

//--------------------------------
// 出品管理の行コンポーネント
//--------------------------------
// React.memo でラップして export する
// React.memoは、「渡されたPropsが変わらない限り、コンポーネントの再レンダリングをスキップする」**ための機能
// 特に行用コンポーネントのように複数レンダリングする場合、Propsの内容を判断して差異がなければ、再レンダリングをスキップするためパフォーマンスが向上する
export const BiddingItemMaintenanceItemRow = memo(
  ({
    item,
    isSplitting,
    isEditing,
    isSaving = false,
    onToggleSplit,
    onToggleEdit,
    onSaveEdit,
    onConfirmSplit,
    onStatusChange,
    onDelete,
    onCopy,
    masterData,
    isSeller,
    isFarmer,
    isSelected = false,
    onToggleSelect,
    isCheckable = false,
  }: BiddingItemMaintenanceItemRowProps) => {
    //----------------
    // Context使用定義
    //----------------
    const { currentUser } = useAuth(); // 認証Context

    //----------------
    // State定義
    //----------------
    const [isMobileDetailsOpen, setIsMobileDetailsOpen] = useState(false); // モバイル用詳細エリア表示状態(true:モバイル用詳細エリア表示中)

    const { tooltipProps, TooltipElement } = useTruncatedTooltip();

    //----------------
    // Memo定義
    //----------------
    //総容量計算
    const displayInfo = useMemo(() => {
      try {
        // 1. 各値を Big オブジェクトに変換
        const cap1 = new Big(item.capacity1 || 0);
        const qty1 = new Big(item.quantity1 || 0);
        const cap2 = new Big(item.capacity2 || 0);
        const qty2 = new Big(item.quantity2 || 0);

        // 2. 10進数で正確に計算： (cap1 * qty1) + (cap2 * qty2)
        const total = cap1.times(qty1).plus(cap2.times(qty2));

        return {
          // 3. 結果を number 型に戻して返す
          totalCapacity: total.toNumber(),
        };
      } catch (error) {
        // 数値として読み込めない値が入った場合のセーフティ
        console.error("Calculation error:", error);
        return { totalCapacity: 0 };
      }
    }, [item.capacity1, item.quantity1, item.capacity2, item.quantity2]); // 依存配列を具体的に指定するとより確実です

    // このアイテムのステータス変更がロックされているかを判断
    const isStatusSelectLocked = useMemo(() => {
      // グローバルロック（保存中など）
      if (isSaving) return true;
      // グローバルロック（保存中など）
      if (isSaving) return true;

      // 提出済(APPLIED)、確定済(CONFIRMED)のアイテムは、特定のロールでは変更不可
      if (item.status === BiddingItemStatusConst.CONFIRMED || item.status === BiddingItemStatusConst.APPLIED) {
        if (
          !!currentUser &&
          (currentUser.role === UserRole.Seller ||
            currentUser.role === UserRole.JA ||
            currentUser.role === UserRole.Farmer)
        ) {
          return true; // seller、JA、farmer は確定済を変更できない
        }
      }

      // それ以外はロックしない
      return false;
    }, [isSaving, item.status, currentUser]);

    //----------------
    // その他の定義
    //----------------
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const rowClasses = "hover:bg-slate-50";
    const currentStatusStyle =
      statusOptions.find((opt) => opt.value === item.status)?.color ||
      "bg-gray-100";
    // onToggleSelect (チェック機能) が有効な時は、個別操作ボタンは非表示にする
    const showActions = !onToggleSelect;

    // ユーザーのロールが、そのオプションを選択「可能」か判断するヘルパー
    const isOptionSelectable = (optionValue: ItemStatusType): boolean => {
      const role = currentUser?.role;

      // admin (roleが seller/farmer 以外) は常に true
      if (role !== UserRole.Seller && role !== UserRole.Farmer && role !== UserRole.JA) {
        return true;
      }

      if (role === UserRole.Seller || role === UserRole.JA) {
        const sellerStates: ItemStatusType[] = [
          BiddingItemStatusConst.INPUT,
          BiddingItemStatusConst.APPLIED,
        ];
        return sellerStates.includes(optionValue);
      }
      if (role === UserRole.Farmer) {
        const brandStates: ItemStatusType[] = [
          BiddingItemStatusConst.INPUT,
          BiddingItemStatusConst.APPLIED,
        ];
        return brandStates.includes(optionValue);
      }

      return false; // デフォルト
    };

    //----------------
    // レンダリング処理
    //----------------
    return (
      <>
        <div
          className={`mt-2 border rounded-lg shadow-sm overflow-hidden  ${isSaving ? "bg-slate-50/50" : "bg-white"} ${isEditing || isSplitting ? "ring-2 ring-indigo-500" : ""}`}
        >
          {/* --- PC版レイアウト (sm:以上で表示) --- */}
          <div
            className={`hidden sm:flex p-4 lg:py-2 items-center gap-4 transition-colors ${rowClasses}`}
          >
            {/* チェックボックスエリア */}
            {onToggleSelect && (
              <div className="flex-shrink-0">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    onToggleSelect();
                  }}
                  disabled={!isCheckable || isSaving}
                  className="h-5 w-5 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500 cursor-pointer"
                />
              </div>
            )}

            <div className="flex-1 flex flex-col ">
              {/* 1段目: 受付No, 日付グループ, 売手生産者グループ */}
              <div className="flex items-center gap-8">
                {/* 受付No */}
                <div className="w-26 text-sm font-black text-slate-800 text-left">
                  No.{item.receptionNumber}
                </div>

                {/* 日付グループ */}
                <div className="flex items-center gap-3 text-base text-slate-600 ">
                  <div className="flex items-center gap-1.5">
                    {/* <Calendar className="w-4 h-4 text-slate-400" /> */}
                    <span className="font-medium">
                      {formatToDateInput(item.receptionDate)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {/* <Calendar className="w-4 h-4 text-indigo-400" /> */}
                    <span className="font-medium">
                      {!!item.biddingDate ? (
                        formatToDateInput(item.biddingDate)
                      ) : (
                        <span className="text-slate-400">未設定</span>
                      )}
                    </span>
                  </div>
                </div>

                {/* 売手・生産者 */}
                <div className="flex items-center gap-6 text-base text-slate-700">
                  <div className="flex items-center gap-1.5 w-72 text-slate-700 ml-1 truncate">
                    <span {...tooltipProps} className="truncate">
                      {item.sellerName}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 w-70 text-slate-700 ml-1 truncate">
                    <span {...tooltipProps} className="truncate">
                      {item.farmerName}
                    </span>
                  </div>
                </div>
              </div>

              {/* --- 2段目: 茶区分 | 茶期 | 茶種 | 品種 | GAP | 荷印 --- */}
              <div className="flex items-center text-sm mt-3">
                <div className="grid grid-cols-[2.5rem_0.8rem_2.8rem_0.8rem_3.1rem_1rem_2.8rem_1rem_3.2rem_1rem_3.1rem_1rem_auto] items-center text-slate-500">
                  {/* 入札No. */}
                  <span className="text-slate-700 text-center">
                    {item.biddingNumber || "-"}
                  </span>
                  <span className="text-slate-300 text-center">|</span>
                  
                  {/* 茶区分 */}
                  <span className="text-slate-700 truncate text-center">
                    {item.teaCategory === TEA_CATEGORY.ARACHA
                      ? "荒茶"
                      : "仕上茶"}
                  </span>
                  <span className="text-slate-300 text-center">|</span>

                  {/* 茶期 */}
                  <span className="text-slate-700 truncate text-center">
                    {item.teaSeasonName || "-"}
                  </span>
                  <span className="text-slate-300 text-center">|</span>

                  {/* 茶種 */}
                  <span className="text-slate-700 truncate text-center">
                    {item.teaTypeName || "-"}
                  </span>
                  <span className="text-slate-300 text-center">|</span>

                  {/* 品種 */}
                  <span {...tooltipProps} className="text-slate-700 truncate text-center">
                    {item.varietyName || "-"}
                  </span>
                  <span className="text-slate-300 text-center">|</span>

                  {/* GAP */}
                  <span className="text-slate-700 truncate text-center">
                    {item.gapName || "-"}
                  </span>
                  <span className="text-slate-300 text-center">|</span>

                  {/* 荷印 */}
                  <div className="text-slate-700 text-base truncate ml-2 w-64">
                    <span {...tooltipProps} className="truncate">
                      {item.brand || "-"}
                    </span>
                  </div>
                </div>
              </div>

              {/* --- 3段目 --- */}
              <div className="flex items-center justify-between mt-1 -mb-1">
                <div className="grid grid-cols-[2.8rem_0.8rem_3.1rem_1rem_2.8rem_1rem_auto] items-center text-slate-700 text-sm">
                  {/* 出品区分 */}
                  <span className="text-slate-700 truncate text-center">
                    {hasSubItems
                      ? "-"
                      : item.listingCategory === ListingCategoryConst.BIDDING
                        ? "入札"
                        : item.listingCategory === ListingCategoryConst.SALE
                          ? "入札会"
                          : "相対"}
                  </span>
                  <span className="text-slate-300 text-center">|</span>

                  {/* 栽培区分 */}
                  <span className="truncate text-center">
                    {CaltivationCategoryOptions.find(
                      (opt) => opt.value === item.caltivationCategory,
                    )?.label || "-"}
                  </span>
                  <span className="text-slate-300 text-center">|</span>

                  {/* 生産履歴 */}
                  <span className="truncate text-center">
                    {ProductionHistoryOptions.find(
                      (opt) => opt.value === item.productionHistory,
                    )?.label || "-"}
                  </span>
                  <span className="text-slate-300 text-center">|</span>

                  {/* 備考 */}
                  <span
                    {...tooltipProps}
                    className="w-110 text-slate-700 !text-sm truncate"
                  >
                    {item.biko || "備考なし"}
                  </span>
                </div>

                {/* 金額・数量エリア */}
                <div className="flex items-end gap-6">
                  {/* 内訳 */}
                  <div className="flex flex-col text-sm text-slate-800 items-end tabular-nums">
                    {/* ①の行 */}
                    {Number(item.capacity1) > 0 &&
                      Number(item.quantity1) > 0 && (
                        <div className="grid grid-cols-[1rem_3rem_1.2rem_1rem_2.5rem_1rem] items-baseline translate-y-0.5">
                          <span className="text-slate-800 text-sm text-left translate-y-0.5">
                            ①
                          </span>
                          {/* 容量：右寄せの固定幅 */}
                          <span className="text-right font-semibold text-sm">
                            {item.capacity1}
                          </span>
                          <span className="text-sm text-slate-800 ml-0.5 text-left">
                            kg
                          </span>
                          {/* 演算子：中央寄せの固定幅 */}
                          <span className="text-slate-800 text-center text-sm translate-y-0.5">
                            ×
                          </span>
                          {/* 本数：右寄せの固定幅 */}
                          <span className="text-right font-semibold text-sm">
                            {item.quantity1}
                          </span>
                          <span className="text-sm text-slate-800 ml-0.5 text-left">
                            本
                          </span>
                        </div>
                      )}

                    {/* ②の行 */}
                    {Number(item.capacity2) > 0 &&
                      Number(item.quantity2) > 0 && (
                        <div className="grid grid-cols-[1rem_3rem_1.2rem_1rem_2.5rem_1rem] items-baseline translate-y-0.5">
                          <span className="text-slate-800 text-sm text-left translate-y-0.5">
                            ②
                          </span>
                          <span className="text-right font-semibold text-sm">
                            {item.capacity2}
                          </span>
                          <span className="text-sm text-slate-800 ml-0.5 text-left">
                            kg
                          </span>
                          <span className="text-slate-800 text-center text-sm translate-y-0.5">
                            ×
                          </span>
                          <span className="text-right font-semibold text-sm">
                            {item.quantity2}
                          </span>
                          <span className="text-sm text-slate-800 ml-0.5 text-left">
                            本
                          </span>
                        </div>
                      )}
                  </div>

                  {/* 総量 */}
                  <div className="text-right w-20 -translate-y-1.5">
                    <span className="text-lg font-bold text-slate-800">
                      {displayInfo.totalCapacity > 0
                        ? displayInfo.totalCapacity
                        : "-"}
                    </span>
                    <span className="text-xs font-medium text-slate-500 ml-1">
                      kg
                    </span>
                  </div>

                  {/* 金額 */}
                  <div className="text-right w-28 -translate-y-1.5">
                    <span className="text-lg font-bold">
                      {Number(item.bidMinPrice) > 0
                        ? item.bidMinPrice.toLocaleString()
                        : "-"}
                    </span>
                    <span className="text-xs font-medium ml-1">円</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ボタンアクションエリア */}
            {showActions && (
              <div className="flex flex-shrink-0 flex-col items-end gap-3 w-32 border-l border-slate-100 pl-4">
                {/* 上段：ステータス選択 (横幅いっぱい) */}
                <select
                  value={item.status}
                  onChange={(e) =>
                    onStatusChange(Number(e.target.value) as ItemStatusType)
                  }
                  onClick={(e) => e.stopPropagation()}
                  disabled={isStatusSelectLocked}
                  className={`w-full px-2 py-1.5 text-sm font-medium bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${currentStatusStyle}`}
                >
                  {statusOptions.map((opt) => (
                    <option
                      key={opt.value}
                      value={opt.value}
                      disabled={
                        opt.value !== item.status &&
                        !isOptionSelectable(opt.value)
                      }
                    >
                      {opt.label}
                    </option>
                  ))}
                </select>
                
               {/* 下段：4つ角配置（背景なし・コンパクト） */}
               <div className="grid grid-cols-3 gap-x-10 gap-y-2 mt-1 pr-1"> 
                 {/* 1. 編集 (左上) */}
                 <div className="relative group flex justify-center">
                   <button
                     onClick={(e) => { e.stopPropagation(); onToggleEdit(); }}
                     disabled={isStatusSelectLocked}
                     className="p-1 text-slate-400 hover:text-indigo-600 transition-colors"
                   >
                     <Pencil className="w-5 h-5" />
                   </button>
                   <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-800 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                     編集
                   </span>
                 </div>
                               
                 {/* 2. コピー (右上) */}
                 <div className="relative group flex justify-center">
                   <button
                     onClick={(e) => { e.stopPropagation(); onCopy(item); }}
                     disabled={isSaving}
                     className="p-1 text-slate-400 hover:text-emerald-600 transition-colors"
                   >
                     <Copy className="w-5 h-5" />
                   </button>
                   <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-800 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                     コピー
                   </span>
                 </div>
                               
                 {/* 3. 分割 (左下) */}
                 {/* <div className="relative group flex justify-center">
                   <button
                     onClick={(e) => { e.stopPropagation(); onToggleSplit(); }}
                     disabled={isStatusSelectLocked}
                     className="p-1 text-slate-400 hover:text-blue-600 transition-colors"
                   >
                     <Rows2 className="w-5 h-5" />
                   </button>
                   <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-800 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                     分割
                   </span>
                 </div> */}
                               
                 {/* 4. 削除 (右下) */}
                 <div className="relative group flex justify-center">
                   <button
                     onClick={(e) => { e.stopPropagation(); onDelete(); }}
                     disabled={isStatusSelectLocked}
                     className="p-1 text-slate-400 hover:text-red-600 transition-colors"
                   >
                     <Trash2 className="w-5 h-5" />
                   </button>
                   <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-800 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                     削除
                   </span>
                 </div>
               </div>
              </div>
            )}

            {/* 一括変更モード時は、ステータス表示だけ出す (操作不可) */}
            {!showActions && (
              <div className="flex ml-4 flex-shrink-0 flex-col items-end gap-2 w-36">
                <div
                  className={`w-full px-3 py-2 text-sm rounded-md text-center font-medium ${currentStatusStyle}`}
                >
                  {
                    statusOptions.find((opt) => opt.value === item.status)
                      ?.label
                  }
                </div>
              </div>
            )}
          </div>

          {/* --- モバイル版レイアウト (sm:未満で表示) --- */}
          <div className="sm:hidden">
            {/* --- モバイル用 初期表示エリア --- */}
            <div
              className={`p-4 ${rowClasses} transition-colors cursor-pointer relative`}
              onClick={() => setIsMobileDetailsOpen((p) => !p)}
            >
              {/* モバイル用チェックボックス */}
              {onToggleSelect && (
                <div className="pt-1" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={onToggleSelect}
                    disabled={!isCheckable || isSaving}
                    className="h-6 w-6 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500 disabled:opacity-50"
                  />
                </div>
              )}

              <div className="flex-1">
                {/* Row 1: 入札日, ステータス */}
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <p className="text-sm text-slate-500">入札日</p>
                    <p className="font-semibold text-lg">
                      {formatToDateInput(item.biddingDate)}
                    </p>
                  </div>
                  <div className="w-32">
                    {/* ステータスプルダウンの幅を固定 */}
                    {showActions ? (
                      <select
                        value={item.status}
                        onChange={(e) =>
                          onStatusChange(
                            Number(e.target.value) as ItemStatusType,
                          )
                        }
                        onClick={(e) => e.stopPropagation()}
                        disabled={isStatusSelectLocked}
                        className={`w-full px-3 py-2 text-sm rounded-md border-0 disabled:opacity-50 disabled:cursor-not-allowed ${currentStatusStyle}`}
                      >
                        {statusOptions.map((opt) => (
                          <option
                            key={opt.value}
                            value={opt.value}
                            disabled={
                              opt.value !== item.status &&
                              !isOptionSelectable(opt.value)
                            }
                          >
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div
                        className={`w-full px-3 py-2 text-sm rounded-md text-center font-medium ${currentStatusStyle}`}
                      >
                        {
                          statusOptions.find((opt) => opt.value === item.status)
                            ?.label
                        }
                      </div>
                    )}
                  </div>
                </div>

                {/* Row 2-4: 売り手, 生産者, 茶種, 品種名, 荷印, 総量 */}
                <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                  <div>
                    <p className="text-sm text-slate-500">売手</p>
                    <p {...tooltipProps}>{item.sellerName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">生産者</p>
                    <p {...tooltipProps}>{item.farmerName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">茶種</p>
                    <p {...tooltipProps}>{item.teaTypeName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">品種名</p>
                    <p {...tooltipProps}>{item.varietyName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">荷印</p>
                    <p {...tooltipProps}>{item.brand}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">総量（kg）</p>
                    <p className="font-semibold">
                      {displayInfo.totalCapacity}
                    </p>
                  </div>
                </div>

                {/* --- モバイル用 展開/折りたたみアイコン --- */}
                <div className="absolute bottom-2 right-2">
                  <svg
                    className={`w-6 h-6 text-slate-400 transition-transform ${isMobileDetailsOpen ? "rotate-180" : ""}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* --- モバイル用 アコーディオン詳細 --- */}
            <div
              className={`transition-all duration-300 ease-in-out overflow-hidden ${isMobileDetailsOpen ? "max-h-116" : "max-h-0"}`}
            >
              <div
                className={`p-4 border-t border-slate-200 ${isSaving ? "bg-slate-50/50" : "bg-white"}`}
              >
                {/* Expanded Row 1-3: 受付日, 茶期, 出品区分, 容量/本数, 最低落札値 */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                    <p className="text-sm text-slate-500">受付No.</p>
                    <p className="font-semibold">{item.receptionNumber}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">入札No.</p>
                    <p className="font-semibold">{item.biddingNumber}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">受付日</p>
                    <p className="font-semibold">
                      {formatToDateInput(item.receptionDate)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">茶期</p>
                    <p className="font-semibold">{item.teaSeasonName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">GAP区分</p>
                    <p className="font-semibold">{item.gapName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">栽培区分</p>
                    <div className="font-semibold">
                      {(() => {
                        const targetOption = CaltivationCategoryOptions.find(
                          (opt) => opt.value === item.caltivationCategory,
                        );
                        return targetOption ? (
                          <p className="font-semibold">{targetOption.label}</p>
                        ) : (
                          <p className="font-semibold text-slate-400">-</p>
                        );
                      })()}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">生産履歴</p>
                    <div className="font-semibold">
                      {(() => {
                        const targetOption = ProductionHistoryOptions.find(
                          (opt) => opt.value === item.productionHistory,
                        );
                        return targetOption ? (
                          <p className="font-semibold">{targetOption.label}</p>
                        ) : (
                          <p className="font-semibold text-slate-400">-</p>
                        );
                      })()}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">茶区分</p>
                    {hasSubItems ? (
                      <p className="font-semibold text-slate-500">-</p>
                    ) : (
                      <p className={`font-semibold`}>
                        {item.teaCategory === TEA_CATEGORY.ARACHA
                          ? "荒茶"
                          : "仕上茶"}
                      </p>
                    )}
                  </div>
                  {/* <div>
                    <p className="text-sm text-slate-500">出品区分</p>
                    {hasSubItems ? (
                      <p className="font-semibold text-slate-500">-</p>
                    ) : (
                      <p className={`font-semibold`}>
                        {item.listingCategory === ListingCategoryConst.BIDDING
                          ? "入札"
                          : "相対"}
                      </p>
                    )}
                  </div> */}
                  <div className="col-span-2">
                    <p className="text-sm text-slate-500">備考</p>
                    <p {...tooltipProps} className="!font-semibold truncate">
                      {item.biko}
                    </p>
                  </div>
                  {/* <div><p className="text-sm text-slate-500">容量/本数</p><p className="font-semibold">{displayInfo.text}</p></div> */}
                  <div>
                    <p className="text-[12px] text-slate-500">
                      容量（kg）/本数（本）
                    </p>
                    {/* ①のペアが有効 (0より大きい) なら表示 */}
                    {Number(item.capacity1) > 0 &&
                    Number(item.quantity1) > 0 ? (
                      <p className="font-semibold text-slate-700">
                        ① {item.capacity1} × {item.quantity1}
                      </p>
                    ) : null}
                    {/* 0やfalsyな場合に '0' をレンダリングしないように null を返す */}
                    {/* ②のペアが有効 (0より大きい) なら表示 */}
                    {Number(item.capacity2) > 0 &&
                    Number(item.quantity2) > 0 ? (
                      <p className="font-semibold text-slate-700">
                        ② {item.capacity2} × {item.quantity2}
                      </p>
                    ) : null}
                    {/* ①も②も有効でない場合 (N/A表示) */}
                    {!(
                      Number(item.capacity1) > 0 && Number(item.quantity1) > 0
                    ) &&
                      !(
                        Number(item.capacity2) > 0 && Number(item.quantity2) > 0
                      ) && <p className="font-semibold text-slate-400">N/A</p>}
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">最低落札値（円）</p>
                    <p className="font-semibold">
                      {item.bidMinPrice.toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* --- モバイル用 アクションボタン --- */}
                {showActions && (
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleEdit();
                      }}
                      disabled={isStatusSelectLocked}
                      className="p-2 rounded-md hover:bg-slate-200 disabled:opacity-50"
                    >
                      編集
                    </button>
                    {/* コピー */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onCopy(item); // 親から渡されたコピー関数を実行
                      }}
                      disabled={isSaving}
                      className="p-2 rounded-md hover:bg-slate-200 disabled:opacity-50"
                    >
                      コピー
                    </button>
                    {/* <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleSplit();
                      }}
                      disabled={isSaving}
                      className="p-2 rounded-md hover:bg-slate-200 disabled:opacity-50"
                    >
                      分割
                    </button> */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                      }}
                      disabled={isStatusSelectLocked}
                      className="p-2 rounded-md text-red-600 hover:bg-red-100 disabled:opacity-50"
                    >
                      削除
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* --- 共通：サブアイテム、分割フォーム、編集フォーム --- */}
          {hasSubItems && !isSplitting && (
            <div className="border-t border-slate-200">
              {item.subItems.map((sub) => (
                <BiddingItemMaintenanceSubItemRow
                  key={sub.branchNumber}
                  subItem={sub}
                />
              ))}
            </div>
          )}
          <div
            className={`transition-all duration-300 ease-in-out ${isSplitting ? "max-h-[70vh]" : "max-h-0"} overflow-y-auto overflow-x-hidden`}
          >
            {isSplitting && (
              <BiddingItemMaintenanceSplitForm
                originalItem={item}
                onConfirm={onConfirmSplit}
                onCancel={() => onToggleSplit()}
              />
            )}
          </div>
          {/* 検索ダイアログみたいな表示 */}
          {isEditing && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
              <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                <div className="overflow-y-auto">
                  <BiddingItemMaintenanceEditForm
                    originalItem={item}
                    onSave={onSaveEdit}
                    onCancel={onToggleEdit}
                    isCreating={false}
                    isSaving={isSaving}
                    masterData={masterData}
                    isSeller={isSeller}
                    isFarmer={isFarmer}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        {TooltipElement}
      </>
    );
  },
);
